/**
 * The Deals home's list: one row per deal, saying in plain words where its
 * memo stands and offering the one next step (DOP-615).
 *
 * The status and the button come from `dealStanding`, so they cannot
 * disagree. The rarer actions (re-run, rename, remove) sit behind "…",
 * and their editors open under the row.
 */

import { FolderOpen, Loader2, MoreHorizontal, Square } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type {
	DealScreeningDeal,
	DealScreeningRunSummary,
} from "../../types/dealScreening";
import { countLabel, formatIsoTimestamp } from "./dealScreeningFormat";
import { type DealStanding, dealStanding } from "./dealStanding";
import { memoVersionText } from "./memoVersionLabel";

export interface DealsTableProps {
	deals: DealScreeningDeal[];
	/** A refetch is in flight; the rows on screen are the previous answer. */
	stale: boolean;
	onRun: (dealId: string) => void;
	/** Stops the memo a deal is writing; resolves once stopped or refused. */
	onStop: (runId: string) => Promise<void>;
	/** Deals whose Run request is in flight. */
	runningIds: ReadonlySet<string>;
	/** Resolve true when stored; the page reports failures. */
	onRename: (dealId: string, name: string) => Promise<boolean>;
	onRemove: (dealId: string) => Promise<boolean>;
	/**
	 * Required sections still to write, by run id (DOP-659): they block the
	 * memo as a blocking field does, and only the full run knows them.
	 */
	sectionsToWrite?: Readonly<Record<string, number>>;
}

const BUTTON =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium disabled:opacity-50";
/** Stop is red text on white: it ends something, it is not the main action. */
const STOP_BUTTON =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#fecdca] bg-white px-3.5 text-sm font-medium text-[#b42318] hover:bg-[#fef3f2] disabled:opacity-50";

const SECONDARY = `${BUTTON} border-gray-200 bg-white text-text-strong hover:bg-gray-50`;
const TEXT_LINK = "text-sm font-medium text-[#004C4B] hover:underline disabled:opacity-50";

/** Pill colours per standing. Every colour also carries its words. */
const STANDING_PILL: Record<DealStanding["kind"], string> = {
	not_run: "border-gray-200 bg-white text-[#5B6472]",
	writing: "border-[#CCE7E6] bg-[#E6F3F3] text-[#004C4B]",
	failed: "border-[#fecdca] bg-[#fef3f2] text-[#b42318]",
	needs_you: "border-[#fedf89] bg-[#fffaeb] text-[#b54708]",
	ready: "border-[#abefc6] bg-[#ecfdf3] text-[#067647]",
	reviewed: "border-transparent bg-[#f2f2f2] text-[#3f4247]",
};

interface RowMenuProps {
	dealName: string;
	disabled: boolean;
	/** Absent before a first memo: the row's own button runs that one. */
	onRunAgain?: () => void;
	onRename: () => void;
	onRemove: () => void;
}

/** Room the "…" menu needs below its button: four items and padding. */
const MENU_HEIGHT = 180;

/** The row's "…": the rarer actions, out of the way of the next step. */
const RowMenu: React.FC<RowMenuProps> = ({
	dealName,
	disabled,
	onRunAgain,
	onRename,
	onRemove,
}) => {
	const [open, setOpen] = useState(false);
	// Opens upward when the row sits too near the bottom of the window for
	// the menu to fit below it (the last deal usually does).
	const [upward, setUpward] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	const toggle = () => {
		const rect = root.current?.getBoundingClientRect();
		setUpward(rect ? window.innerHeight - rect.bottom < MENU_HEIGHT : false);
		setOpen((value) => !value);
	};
	useEffect(() => {
		if (!open) return;
		const close = (event: MouseEvent) => {
			if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, [open]);
	const item = (label: string, act: () => void, danger = false) => (
		<li role="none">
			<button
				type="button"
				role="menuitem"
				onClick={() => {
					setOpen(false);
					act();
				}}
				className={`block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50 ${
					danger ? "text-[#b42318]" : "text-text-strong"
				}`}
			>
				{label}
			</button>
		</li>
	);
	return (
		<div ref={root} className="relative">
			<button
				type="button"
				onClick={toggle}
				disabled={disabled}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={`More actions for ${dealName}`}
				className={`${SECONDARY} w-9 justify-center px-0`}
			>
				<MoreHorizontal className="h-4 w-4" aria-hidden />
			</button>
			{open && (
				<ul
					role="menu"
					className={`absolute right-0 z-20 w-48 rounded-lg border border-gray-200 bg-white p-1 shadow-lg ${
						upward ? "bottom-full mb-1" : "top-full mt-1"
					}`}
				>
					{onRunAgain && item("Re-run", onRunAgain)}
					{item("Rename", onRename)}
					{item("Remove", onRemove, true)}
				</ul>
			)}
		</div>
	);
};

/** The run page; `from` names the workflow tab the back link returns to. */
export const runHref = (runId: string, tab?: string, from?: string): string => {
	const params = new URLSearchParams();
	if (tab) params.set("tab", tab);
	if (from) params.set("from", from);
	const query = params.toString();
	return `/workflows/deal-screening/runs/${encodeURIComponent(runId)}${query ? `?${query}` : ""}`;
};

/**
 * "3 read · 1 excluded · 2 web pages" when the run says so, and nothing when
 * it does not. `files_read` counts the web pages the run searched too, so
 * they are taken out and named on their own: a web page is not a file in the
 * folder, and counting it as one reads as more files read than exist (DOP-645).
 * The file count beside it is every file in the folder, so every file that
 * was not read is accounted for here. A finished memo the screener is judged
 * against is excluded on purpose. Without saying so, the two numbers read as
 * a file that went missing (DOP-555).
 */
export function filesReadLine(run: DealScreeningRunSummary | null): string | null {
	const read = run?.counts.files_read;
	if (typeof read !== "number") return null;
	const web = run?.counts.files_web ?? 0;
	const parts = [`${Math.max(read - web, 0)} read`];
	const excluded = run?.counts.files_skipped ?? 0;
	if (excluded > 0) parts.push(`${excluded} excluded`);
	const unreadable = run?.counts.files_unreadable ?? 0;
	if (unreadable > 0) parts.push(`${unreadable} unreadable`);
	if (web > 0) parts.push(countLabel(web, "web page"));
	return parts.join(" · ");
}

/** What is open on the memo, blocking first: it is what stops the memo. */
export function needsReviewSummary(
	run: DealScreeningRunSummary | null,
	sectionsToWrite = 0,
): string {
	if (!run || run.generating) return "—";
	const parts: string[] = [];
	const blocking = run.blocking_open + sectionsToWrite;
	if (blocking > 0) {
		parts.push(countLabel(blocking, "blocking gap"));
	}
	if (run.conflicts_open > 0) {
		parts.push(countLabel(run.conflicts_open, "conflict"));
	}
	// Blocking members are gaps too, so naming "gaps" beside a blocking count
	// would count one missing field twice. Gaps are only named on their own.
	if (parts.length === 0 && run.gaps_open > 0) {
		parts.push(`${run.gaps_open} not found`);
	}
	// A required section is already in the blocking count: not said twice.
	const omitted = Math.max((run.counts.sections_omitted ?? 0) - sectionsToWrite, 0);
	if (omitted > 0) parts.push(`${countLabel(omitted, "section")} left out`);
	return parts.length > 0 ? parts.join(" · ") : "Nothing open";
}

/** The line under the deal's name: its files and its latest memo. */
function subLine(deal: DealScreeningDeal): string {
	const run = deal.latest_run;
	const parts = [countLabel(deal.file_count, "file")];
	const read = filesReadLine(run);
	if (read) parts.push(read);
	if (run && !run.generating && run.generated_at) {
		parts.push(`${memoVersionText(run)} written ${formatIsoTimestamp(run.generated_at)}`);
	}
	return parts.join(" · ");
}

interface NextStepProps {
	deal: DealScreeningDeal;
	standing: DealStanding;
	running: boolean;
	onRun: () => void;
	onStop: (runId: string) => Promise<void>;
}

/** The one button a row offers: what the person would do next. */
const NextStep: React.FC<NextStepProps> = ({ deal, standing, running, onRun, onStop }) => {
	const run = deal.latest_run;
	const [stopping, setStopping] = useState(false);
	if (running) {
		// The status already says it is running, so the button is the one
		// thing a person can do about it: stop it. A run requested this
		// moment has no run id yet and cannot be stopped until it does.
		const runId = run?.generating ? run.public_id : null;
		return (
			<button
				type="button"
				disabled={!runId || stopping}
				onClick={() => {
					if (!runId) return;
					setStopping(true);
					void onStop(runId).finally(() => setStopping(false));
				}}
				aria-label={`Stop ${deal.name}`}
				className={`${STOP_BUTTON}`}
			>
				<Square className="h-3 w-3 fill-current" aria-hidden />
				{stopping ? "Stopping…" : "Stop"}
			</button>
		);
	}
	if (!run || standing.kind === "failed") {
		return (
			<button
				type="button"
				onClick={onRun}
				aria-label={`${run ? "Try again" : "Run"} ${deal.name}`}
				className={`${SECONDARY} border-[#99CFCD] text-[#004C4B]`}
			>
				{run ? "Try again" : "Screen it"}
			</button>
		);
	}
	// One word for every memo that exists, whether or not something in it
	// needs the person: the status column already says which, and what needs
	// them is marked in the memo and one click away on Gaps.
	return (
		<Link to={runHref(run.public_id)} aria-label={`Open ${deal.name}`} className={SECONDARY}>
			Open
		</Link>
	);
};

const DealsTable: React.FC<DealsTableProps> = ({
	deals,
	stale,
	onRun,
	onStop,
	runningIds,
	onRename,
	onRemove,
	sectionsToWrite = {},
}) => {
	// One row edits at a time: renaming (with its draft), choosing sections
	// (with the excluded set), or confirming a remove.
	const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
	const [confirmingId, setConfirmingId] = useState<string | null>(null);
	const [busyId, setBusyId] = useState<string | null>(null);

	const saveName = async (dealId: string) => {
		if (!editing || editing.id !== dealId) return;
		const name = editing.name.trim();
		if (name === "") return;
		setBusyId(dealId);
		try {
			if (await onRename(dealId, name)) setEditing(null);
		} finally {
			setBusyId(null);
		}
	};

	const remove = async (dealId: string) => {
		setBusyId(dealId);
		try {
			if (await onRemove(dealId)) setConfirmingId(null);
		} finally {
			setBusyId(null);
		}
	};

	if (deals.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-text-muted">
				No deals yet. Press "Screen a deal" and pick a Deal Room folder.
			</p>
		);
	}

	return (
		<div
			// No overflow clipping: a row's "…" menu must be able to hang below
			// the last row. The header carries the top corners instead.
			className={`rounded-xl border border-gray-200 bg-white ${
				stale ? "opacity-60" : ""
			}`}
			data-testid="deals-table"
		>
			<div className="flex items-center rounded-t-xl border-b border-gray-200 bg-[#fafafa] px-5 py-2.5 text-xs font-semibold text-[#5B6472]">
				<span className="flex-1">Deal</span>
				<span className="hidden w-64 md:block">Where it stands</span>
				<span className="w-44 text-right">Next step</span>
			</div>
			<ul>
				{deals.map((deal) => {
					const run = deal.latest_run;
					const toWrite = run ? (sectionsToWrite[run.public_id] ?? 0) : 0;
					const standing = dealStanding(run, toWrite);
					const running = runningIds.has(deal.public_id) || Boolean(run?.generating);
					const busy = busyId === deal.public_id;
					const pillKind = running ? "writing" : standing.kind;
					return (
						<li
							key={deal.public_id}
							className={`border-b border-[#f1f2f4] last:rounded-b-xl last:border-0 ${
								standing.kind === "needs_you" ? "bg-[#fffdf7]" : ""
							}`}
							data-testid={`deal-row-${deal.public_id}`}
						>
							<div className="flex items-center gap-3.5 px-5 py-3.5">
								<span
									className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
										pillKind === "reviewed" || pillKind === "not_run"
											? "bg-[#f2f2f2] text-[#6B7280]"
											: "bg-[#E6F3F3] text-[#004C4B]"
									}`}
								>
									<FolderOpen className="h-4 w-4" aria-hidden />
								</span>
								<div className="min-w-0 flex-1">
									{editing?.id === deal.public_id ? (
										<form
											onSubmit={(event) => {
												event.preventDefault();
												void saveName(deal.public_id);
											}}
											className="flex items-center gap-2"
										>
											<input
												aria-label={`Name for ${deal.name}`}
												value={editing.name}
												onChange={(event) =>
													setEditing({ id: deal.public_id, name: event.target.value })
												}
												onKeyDown={(event) => {
													if (event.key === "Escape") setEditing(null);
												}}
												// eslint-disable-next-line jsx-a11y/no-autofocus
												autoFocus
												className="w-56 rounded-lg border border-gray-300 px-2.5 py-1.5 text-base text-text-base focus:outline-none focus:ring-2 focus:ring-[#008782]"
											/>
											<button
												type="submit"
												disabled={busy || editing.name.trim() === ""}
												className={TEXT_LINK}
											>
												Save
											</button>
											<button
												type="button"
												onClick={() => setEditing(null)}
												disabled={busy}
												className="text-sm text-text-muted hover:underline"
											>
												Cancel
											</button>
										</form>
									) : (
										<p className="truncate text-[14px] font-semibold text-[#111111]">
											{deal.name}
										</p>
									)}
									<p className="mt-0.5 truncate text-xs text-[#6B7280]">
										{deal.folder_name ?? `Folder ${deal.folder_id}`}/ · {subLine(deal)}
									</p>
								</div>
								<div className="hidden w-64 md:block">
									<span
										className={`inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold ${STANDING_PILL[pillKind]}`}
									>
										{running && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
										{running ? "Running…" : standing.label}
									</span>
									{standing.kind === "needs_you" && !running && (
										<p className="mt-1 text-xs text-[#6B7280]">{needsReviewSummary(run, toWrite)}</p>
									)}
								</div>
								<div className="flex w-44 items-center justify-end gap-2">
									<NextStep
										deal={deal}
										standing={standing}
										running={running}
										onRun={() => onRun(deal.public_id)}
										onStop={onStop}
									/>
									<RowMenu
										dealName={deal.name}
										disabled={running || editing !== null}
										onRunAgain={
											run && standing.kind !== "failed"
												? () => onRun(deal.public_id)
												: undefined
										}
										onRename={() => setEditing({ id: deal.public_id, name: deal.name })}
										onRemove={() => setConfirmingId(deal.public_id)}
									/>
								</div>
							</div>
							{confirmingId === deal.public_id && (
								<div className="mx-5 mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm">
									<span className="flex-1 text-text-strong">
										Remove {deal.name}? Its memos go; the folder stays.
									</span>
									<button
										type="button"
										onClick={() => setConfirmingId(null)}
										disabled={busy}
										className={SECONDARY}
									>
										Keep
									</button>
									<button
										type="button"
										onClick={() => void remove(deal.public_id)}
										disabled={busy}
										className={`${BUTTON} border-[#b42318] bg-[#b42318] text-white hover:bg-[#912018]`}
									>
										Remove
									</button>
								</div>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default DealsTable;
